import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import axios from 'axios'
import StarRating from '../components/StarRating'
import ThumbsRating from '../components/ThumbsRating'
import QuestChat from '../components/QuestChat'
import './ReviewPage.css'

export default function ReviewPage({ user, onLevelUp }) {
  const { gameId } = useParams()
  const navigate = useNavigate()
  const [game, setGame] = useState(null)
  const [existingReview, setExistingReview] = useState(null)

  // Form state
  const [score, setScore] = useState(5)
  const [stars, setStars] = useState(3)
  const [thumbs, setThumbs] = useState(true)
  const [reviewText, setReviewText] = useState('')
  const [status, setStatus] = useState('published')

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    loadData()
  }, [gameId])

  const loadData = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [gameResponse, reviewResponse] = await Promise.all([
        axios.get(`/api/games/${gameId}`),
        axios.get(`/api/reviews/game/${gameId}`, { withCredentials: true })
      ])

      setGame(gameResponse.data)

      if (reviewResponse.data) {
        const review = reviewResponse.data
        setExistingReview(review)
        setScore(review.score_out_of_10)
        setStars(review.stars_out_of_5)
        setThumbs(review.thumbs_up)
        setReviewText(review.review_text || '')
        setStatus(review.status || 'published')
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      setLoadError(error.response?.status === 404 ? 'game-not-found' : 'load-failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e, draftMode = false) => {
    e.preventDefault()
    if (!game) {
      setLoadError('game-not-found')
      return
    }
    setSubmitting(true)

    try {
      const reviewData = {
        game_id: parseInt(gameId),
        game_name: game.name,
        game_cover_url: game.background_image,
        game_platform: game.platforms?.[0]?.platform?.name,
        game_release_year: game.released ? new Date(game.released).getFullYear() : null,
        score_out_of_10: score,
        stars_out_of_5: stars,
        thumbs_up: thumbs,
        review_text: reviewText,
        status: draftMode ? 'draft' : 'published'
      }

      const response = await axios.post('/api/reviews', reviewData, {
        withCredentials: true
      })

      // Check if user leveled up
      const currentLevel = user.current_level || 1
      const newLevel = response.data.user_stats.current_level
      const unlockedBadges = response.data.unlocked_badges || []
      if (newLevel > currentLevel) {
        onLevelUp(newLevel, response.data.user_stats.total_xp, unlockedBadges)
      }

      // Navigate back to game page, scroll to Quest chat to show evaluation
      navigate(`/game/${gameId}`, { state: { scrollToQuest: true } })
    } catch (error) {
      console.error('Failed to submit review:', error)
      alert('Failed to submit review. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const appendDictationToReview = (transcript) => {
    if (!transcript?.trim()) return

    setReviewText(prev => {
      const separator = prev.trim() ? '\n\n' : ''
      return `${prev}${separator}${transcript.trim()}`
    })
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="review-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  if (loadError || !game) {
    return (
      <div className="page-container">
        <div className="container">
          <Link to="/" className="back-link">
            ← Back to Games
          </Link>
          <div className="review-loading">
            <h1 className="page-title">Game unavailable</h1>
            <p>
              {loadError === 'game-not-found'
                ? 'This game could not be loaded right now, so the review form is disabled.'
                : 'We could not load this game. Please try again in a moment.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="page-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="container">
        <Link to={`/game/${gameId}`} className="back-link">
          ← Back to Game
        </Link>

        <div className="review-header">
          <h1 className="page-title">
            {existingReview ? 'Edit Review' : 'Write Review'}
          </h1>
          {game && (
            <div className="review-game-info">
              {game.background_image && (
                <img src={game.background_image} alt={game.name} className="review-game-thumb" />
              )}
              <div>
                <h2>{game.name}</h2>
                {game.released && (
                  <p className="review-game-year">
                    {new Date(game.released).getFullYear()}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="review-workspace">
          <form className="review-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <label className="form-label">
                Your Score (out of 10):
                <span className="score-display">{score}/10</span>
              </label>
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={score}
                onChange={(e) => setScore(parseFloat(e.target.value))}
                className="score-slider"
              />
            </div>

            <div className="form-section">
              <label className="form-label">Your Stars:</label>
              <StarRating value={stars} onChange={setStars} />
            </div>

            <div className="form-section">
              <label className="form-label">Do you recommend it?</label>
              <ThumbsRating value={thumbs} onChange={setThumbs} />
            </div>

            <div className="form-section">
              <label className="form-label" htmlFor="review-text">
                Review draft
              </label>
              <textarea
                id="review-text"
                className="textarea"
                placeholder="Talk to Quest or type what you thought about this game..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows={8}
              />
            </div>

            <div className="form-actions">
              <motion.button
                type="button"
                className="btn btn-secondary btn-large"
                onClick={(e) => handleSubmit(e, true)}
                disabled={submitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                💾 Save Draft
              </motion.button>

              <motion.button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={submitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {submitting ? 'Publishing...' : '✅ Publish Review'}
              </motion.button>
            </div>
          </form>

          <aside className="review-quest-panel" aria-label="Quest review helper">
            <div className="review-quest-heading">
              <h2>Quest review helper</h2>
              <p>Speak to Quest here; final voice transcripts are added to your review draft.</p>
            </div>
            <QuestChat
              gameId={gameId}
              gameName={game.name}
              user={user}
              onDictation={appendDictationToReview}
            />
          </aside>
        </div>
      </div>
    </motion.div>
  )
}
